import { ResponsiveTable, ResponsiveCell } from "../components/ResponsiveTable";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { formatMoney, type Product, type StockMovement } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ProductStatusBadge } from "../components/ProductStatusBadge";
import { StockAdjustModal } from "../components/StockAdjustModal";

/** A product is "low" when on-hand has fallen to or below its threshold. */
function isLowStock(p: Product): boolean {
  return p.quantity <= p.lowStockThreshold;
}

export function InventoryList() {
  const api = useApi();
  const load = useCallback(() => api.listProducts(), [api]);
  const { data, loading, error, reload, setData } = useAsync<Product[]>(load, [load]);

  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [adjusting, setAdjusting] = useState<Product | null>(null);
  const [toUnpublish, setToUnpublish] = useState<Product | null>(null);
  const [toDelete, setToDelete] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);

  const replaceProduct = (updated: Product) => {
    setData((prev) => (prev ? prev.map((p) => (p.id === updated.id ? updated : p)) : prev));
  };

  const onPublish = async (product: Product) => {
    setActingId(product.id);
    setActionError(undefined);
    try {
      replaceProduct(await api.publishProduct(product.id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setActingId(null);
    }
  };

  const confirmUnpublish = async () => {
    if (!toUnpublish) return;
    setBusy(true);
    setActionError(undefined);
    try {
      replaceProduct(await api.unpublishProduct(toUnpublish.id));
      setToUnpublish(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unpublish failed.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setBusy(true);
    setActionError(undefined);
    try {
      await api.deleteProduct(toDelete.id);
      const deletedId = toDelete.id;
      setData((prev) => (prev ? prev.filter((p) => p.id !== deletedId) : prev));
      setToDelete(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const onAdjusted = (result: { product: Product; movement: StockMovement }) => {
    replaceProduct(result.product);
    setAdjusting(null);
  };

  if (loading) return <Spinner label="Loading products…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;
  if (!data) return null;

  const products = lowStockOnly ? data.filter(isLowStock) : data;
  const lowStockCount = data.filter(isLowStock).length;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Inventory</h1>
          <p className="page__subtitle">
            {data.length} {data.length === 1 ? "product" : "products"}
            {lowStockCount > 0 ? ` · ${lowStockCount} low on stock` : ""}
          </p>
          <p className="page__subtitle">Manage book pages, descriptions, pricing and stock here.</p>
        </div>
        <div className="page__actions">
          <Link to="/products/new" className="btn btn--primary">
            New product
          </Link>
        </div>
      </div>

      {actionError ? <ErrorBanner title="Action failed" message={actionError} /> : null}

      {data.length === 0 ? (
        <div className="empty-state">
          <p>No products yet.</p>
          <Link to="/products/new" className="btn btn--primary">
            Create the first product
          </Link>
        </div>
      ) : (
        <>
          <div className="form-field form-field--check">
            <label>
              <input
                type="checkbox"
                checked={lowStockOnly}
                onChange={(e) => setLowStockOnly(e.target.checked)}
              />{" "}
              Low stock only
            </label>
          </div>

          {products.length === 0 ? (
            <div className="empty-state">
              <p>No products are low on stock.</p>
            </div>
          ) : (
            <ResponsiveTable label="Inventory">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Name</th>
                  <th scope="col" role="columnheader">SKU</th>
                  <th scope="col" role="columnheader">Price</th>
                  <th scope="col" role="columnheader">On hand</th>
                  <th scope="col" role="columnheader">Status</th>
                  <th scope="col" role="columnheader" className="table__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {products.map((product) => {
                  const acting = actingId === product.id;
                  const low = isLowStock(product);
                  return (
                    <tr role="row" key={product.id}>
                      <ResponsiveCell label="Name">
                        <Link to={`/products/${product.id}/edit`} className="table__link">
                          {product.name}
                        </Link>
                      </ResponsiveCell>
                      <ResponsiveCell label="SKU" className="muted">{product.sku}</ResponsiveCell>
                      <ResponsiveCell label="Price">{formatMoney(product.priceCents, product.currency)}</ResponsiveCell>
                      <ResponsiveCell label="On hand" className="table__badges">
                        {product.quantity}
                        {low ? (
                          <span className="badge badge--warning" title="At or below the low-stock threshold">
                            Low stock
                          </span>
                        ) : null}
                      </ResponsiveCell>
                      <ResponsiveCell label="Status">
                        <ProductStatusBadge status={product.status} />
                      </ResponsiveCell>
                      <ResponsiveCell label="Actions" className="table__actions">
                        <Link
                          to={`/products/${product.id}/edit`}
                          className="btn btn--secondary btn--sm"
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setAdjusting(product)}
                          disabled={acting}
                        >
                          Adjust
                        </button>
                        {product.status === "archived" ? (
                          <>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => void onPublish(product)}
                              disabled={acting}
                            >
                              {acting ? "Publishing…" : "Publish"}
                            </button>
                            <button
                              type="button"
                              className="btn btn--danger btn--sm"
                              onClick={() => setToDelete(product)}
                              disabled={acting}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setToUnpublish(product)}
                            disabled={acting}
                          >
                            Unpublish
                          </button>
                        )}
                      </ResponsiveCell>
                    </tr>
                  );
                })}
              </tbody>
            </ResponsiveTable>
          )}
        </>
      )}

      {adjusting ? (
        <StockAdjustModal
          product={adjusting}
          onClose={() => setAdjusting(null)}
          onAdjusted={onAdjusted}
        />
      ) : null}

      {toUnpublish ? (
        <ConfirmDialog
          title="Unpublish product"
          message={`Unpublish “${toUnpublish.name}”? It will be removed from the storefront until re-published.`}
          confirmLabel="Unpublish"
          danger
          busy={busy}
          onConfirm={() => void confirmUnpublish()}
          onCancel={() => setToUnpublish(null)}
        />
      ) : null}

      {toDelete ? (
        <ConfirmDialog
          title="Delete product"
          message={`Delete “${toDelete.name}”? This permanently removes the product and its stock history. This cannot be undone.`}
          confirmLabel="Delete"
          danger
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setToDelete(null)}
        />
      ) : null}
    </div>
  );
}
